/**
 * DirhamPrice — the ONE component that renders any AED amount across the app.
 *
 * Requirements (iter-217c-v11, per user directive):
 *   • Icon replaces the text "AED" everywhere (invoices, posts, dashboards…)
 *   • Icon is ALWAYS on the LEFT of the numeric price (both AR & EN pages).
 *   • Colour auto-adapts to background:
 *       - `theme="dark"`  → white icon (default; matches app-wide dark theme)
 *       - `theme="light"` → black icon (for PDF exports, printouts, story
 *                          overlays on light backgrounds)
 *       - `theme="auto"`  → uses CSS `mix-blend-mode: difference` so the icon
 *                          renders visible on ANY bg (last-resort fallback)
 *   • Rendered as `dir="ltr"` regardless of parent direction so the "icon
 *     then number" ordering never flips on Arabic RTL layouts.
 *
 * Usage:
 *   <DirhamPrice value={4200} />                    // 4,200 with white icon
 *   <DirhamPrice value={4200} theme="light" />      // black icon (light bg)
 *   <DirhamPrice value={99.5} decimals={2} />       // 99.50
 *   <DirhamPrice value={0} placeholder="—" />       // Explicit empty label
 *   <DirhamPrice value={4200} size="lg" />          // Larger icon+text
 */
import React from 'react';

const _SIZE_MAP = {
  xs: { icon: 'h-2.5 w-2.5', gap: 'gap-0.5', text: 'text-xs' },
  sm: { icon: 'h-3 w-3',   gap: 'gap-1',   text: 'text-sm' },
  md: { icon: 'h-4 w-4',   gap: 'gap-1',   text: 'text-base' },
  lg: { icon: 'h-5 w-5',   gap: 'gap-1.5', text: 'text-lg' },
  xl: { icon: 'h-6 w-6',   gap: 'gap-2',   text: 'text-xl' },
  '2xl': { icon: 'h-8 w-8', gap: 'gap-2', text: 'text-2xl' },
};

const DIRHAM_BLACK = '/currency/dirham-black.png';
const DIRHAM_WHITE = '/currency/dirham-white.png';

function _formatNumber(value, decimals) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0';
  // iter-237 (Feb 2026) — TRUNCATE first (never round). User policy: 1153.4964
  // must display as 1153.49, not 1153.50. `toLocaleString` alone rounds.
  const factor = Math.pow(10, decimals);
  const sign = n < 0 ? -1 : 1;
  const truncated = (sign * Math.floor(Math.abs(n) * factor + 1e-6)) / factor;
  return truncated.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export const DirhamPrice = ({
  value,
  theme = 'dark',      // 'dark' | 'light' | 'auto'
  size = 'md',
  decimals = 0,
  className = '',
  bold = false,
  placeholder = null,
  dataTestId,
}) => {
  const sz = _SIZE_MAP[size] || _SIZE_MAP.md;
  const n = Number(value);
  if ((!Number.isFinite(n) || n === 0) && placeholder) {
    return <span className={`${sz.text} ${className}`} data-testid={dataTestId}>{placeholder}</span>;
  }

  const iconSrc = theme === 'light' ? DIRHAM_BLACK : DIRHAM_WHITE;
  const blendClass = theme === 'auto' ? 'mix-blend-difference' : '';
  const weight = bold ? 'font-bold' : 'font-medium';

  return (
    <span
      dir="ltr"
      className={`inline-flex items-center ${sz.gap} ${sz.text} ${weight} ${className}`}
      data-testid={dataTestId || 'dirham-price'}
    >
      <img
        src={iconSrc}
        alt="AED"
        className={`${sz.icon} ${blendClass} object-contain select-none`}
        draggable={false}
        aria-hidden="true"
      />
      <span data-testid={dataTestId ? `${dataTestId}-value` : 'dirham-price-value'}>
        {_formatNumber(n, decimals)}
      </span>
    </span>
  );
};

// A raw <img> for cases where the caller lays out the number itself
// (e.g., huge inline hero text). Same theme rules as DirhamPrice.
export const DirhamIcon = ({ theme = 'dark', size = 'md', className = '' }) => {
  const sz = _SIZE_MAP[size] || _SIZE_MAP.md;
  const src = theme === 'light' ? DIRHAM_BLACK : DIRHAM_WHITE;
  const blend = theme === 'auto' ? 'mix-blend-difference' : '';
  return (
    <img
      src={src}
      alt="AED"
      className={`${sz.icon} ${blend} object-contain inline-block select-none ${className}`}
      draggable={false}
      aria-hidden="true"
    />
  );
};

export default DirhamPrice;
